<template>
  <div class="d-flex flex-row" style="height: 90vh;"> <!-- Added height -->
    <div class="d-flex flex-column data-column">
      <!-- Search Field -->
      <v-text-field
        v-model="searchQuery"
        label="Search by Page ID"
        prepend-inner-icon="mdi-magnify"
        clearable
        class="mb-2"
        :loading="searching"
        @input="debouncedSearch"
        filled
        hide-details
      ></v-text-field>

      <!-- Classification Filter -->
      <v-btn-toggle
        v-model="classificationFilter"
        mandatory
        dense
        class="mb-2"
        group
      >
        <v-btn value="ALL">
          All
        </v-btn>
        <v-btn value="GOOD" color="green lighten-4 green--text text--darken-2">
          Good
        </v-btn>
        <v-btn value="BAD" color="red lighten-4 red--text text--darken-2">
          Bad
        </v-btn>
      </v-btn-toggle>

      <!-- Tree View (now a list of Orders/Pages) -->
      <v-treeview
        v-model="active"
        :items="treeItems"
        :open.sync="open"
        activatable
        open-on-click
        item-key="id"
        return-object
        @update:active="handleActiveChange"
        :search="searchQuery"
        :filter="filterTree"
        class="flex-grow-1"
        style="overflow-y: auto;"
      >
        <template v-slot:prepend="{ item }">
          <v-icon :color="getIconColorByClassification(item.data.classification)">
            mdi-file-document-outline
          </v-icon>
        </template>
        <template v-slot:label="{ item }">
          <span :style="{ color: getItemColor(item) }">{{ item.name }}</span>
        </template>
      </v-treeview>

      <!-- Loading / Pagination Controls -->
      <div v-if="bulkLoading" class="mt-2 flex-shrink-0">
        <v-progress-linear
          :value="progressPercentage"
          height="10"
          class="mb-2"
        >
          <strong>{{ progressPercentage }}%</strong>
        </v-progress-linear>
        <v-btn block color="error" @click="stopBulkLoading">
          Stop
        </v-btn>
      </div>
      <div v-else-if="!searchQuery && !loading && !bulkLoading" class="mt-2 flex-shrink-0">
        <v-btn
          v-if="hasMore"
          @click="loadMoreOrders"
          block
          class="mb-2"
          :disabled="loading"
        >
          Load Next ({{ orders.length }}/{{ totalOrders > 0 ? totalOrders : '?' }}) <!-- Improved count display -->
        </v-btn>
        <v-btn
          v-if="hasMore"
          @click="loadAllOrders"
          block
          class="mt-2"
          :disabled="loading"
        >
          Load All
        </v-btn>
      </div>
      <v-progress-linear
        v-else-if="loading || searching"
        indeterminate
        class="mt-2 flex-shrink-0"
      ></v-progress-linear>
    </div>

    <!-- Preview/Details Panel -->
    <div class="d-flex ml-4 flex-1 the-thin-grey-border grey lighten-4 flex-column">
      <div>
        <v-tabs v-model="selectedTab" background-color="white">
          <v-tab :disabled="!imagePreview">Image Preview</v-tab>
          <v-tab :disabled="!documentPreview">Source Preview</v-tab>
          <v-tab>Order Details</v-tab>
        </v-tabs>
      </div>
      <v-divider></v-divider>
      <v-tabs-items v-model="selectedTab" class="grey lighten-4" style="height: 100%">
        <v-tab-item class="text-left">
          <img v-if="imagePreview" :src="imagePreview" width="100%" height="100%">
          <div v-else class="d-flex justify-center align-center fill-height grey--text">No image preview available</div>
        </v-tab-item>
        <v-tab-item class="text-left" style="height: 100%;">
          <iframe
             v-if="documentPreview"
             :src="documentPreview"
             width="100%"
             style="height: 100%; border: none; display: block;"
           ></iframe>
          <div v-else></div>
        </v-tab-item>
        <v-tab-item class="text-left">
          <pre v-if="selectedOrder" style="white-space: pre-wrap; word-wrap: break-word;">{{ JSON.stringify(selectedOrder.orderData, null, 2) }}</pre>
        </v-tab-item>
      </v-tabs-items>
    </div>
  </div>
</template>

<script>
import {
  getFirestore,
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  where,
  getCountFromServer
} from "firebase/firestore";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import debounce from "lodash/debounce";

// --- Constants ---
const FIRESTORE_COLLECTION = "op_switchorders_otos";
const PAGE_SIZE = 100;
const ORDER_FIELD = "page_id"; // Field to order/search by

// --- Expected field names in Firestore documents ---
const PAGE_ID_FIELD = "page_id";
const CLASSIFICATION_FIELD = "classification";
// const IMAGE_PATH_FIELD = "jpg_filepath"; // No longer directly used for path
const DOCUMENT_PATH_FIELD = "source_filepath"; // Still used for source PDF/doc

export default {
  name: 'SwitchOrdersOtosExplorer',
  data() {
    return {
      searchQuery: "",
      imagePreview: null,
      documentPreview: null,
      orders: [],
      active: [],
      open: [],
      loading: false,
      searching: false,
      lastDoc: null,
      hasMore: true,
      pageSize: PAGE_SIZE,
      totalOrders: 0,
      bulkLoading: false,
      stopBulk: false,
      selectedOrder: null,
      selectedTab: 0,
      classificationFilter: 'ALL',
      previewLoading: null,
    };
  },
  methods: {
    async loadOrders() {
      if (this.loading || this.stopBulk) return;
      this.loading = true;
      // --- Add logging for filter debugging ---
      console.log(
          `Loading orders... Filter: ${this.classificationFilter}, ` +
          `StartAfter: ${this.lastDoc ? this.lastDoc.id : 'None'}`
      );
      // --- End logging ---
      try {
        const db = getFirestore();
        const baseCollectionRef = collection(db, FIRESTORE_COLLECTION);

        const queryConstraints = [orderBy(ORDER_FIELD)];

        if (this.classificationFilter !== 'ALL') {
          // --- Log index requirement ---
          console.log(`Applying filter: ${CLASSIFICATION_FIELD} == ${this.classificationFilter}. Ensure Firestore index exists: (${CLASSIFICATION_FIELD} ASC, ${ORDER_FIELD} ASC)`);
          // --- End log ---
          queryConstraints.unshift(where(CLASSIFICATION_FIELD, "==", this.classificationFilter));
        }

        if (this.lastDoc) {
          queryConstraints.push(startAfter(this.lastDoc));
        }
        queryConstraints.push(limit(this.pageSize));

        const q = query(baseCollectionRef, ...queryConstraints);
        // --- Log the final query constraints ---
        console.log('Firestore Query Constraints:', queryConstraints);
        // --- End log ---

        const ordersSnapshot = await getDocs(q);
        // --- Log if snapshot is empty ---
        if (ordersSnapshot.empty) {
            console.log('Firestore query returned no documents.');
        }
        // --- End log ---

        const newOrders = ordersSnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          orderData: docSnap.data()
        }));

        console.log(`Loaded ${newOrders.length} orders.`);

        const currentOrderIds = new Set(this.orders.map(o => o.id));
        const uniqueNewOrders = newOrders.filter(o => !currentOrderIds.has(o.id));
        this.orders = [...this.orders, ...uniqueNewOrders];

        this.lastDoc = ordersSnapshot.docs[ordersSnapshot.docs.length - 1] || this.lastDoc;
        this.hasMore = ordersSnapshot.docs.length === this.pageSize;
        console.log("Has more:", this.hasMore);

        if (!this.lastDoc && this.classificationFilter === 'ALL' && this.totalOrders === 0) {
             await this.getTotalOrderCount(); // Use await
        } else if (this.classificationFilter !== 'ALL' && !this.lastDoc) {
            // Update count when filtering (if it's the first page)
            await this.getTotalOrderCount(true); // Use await
        }


      } catch (error) {
        // --- Log Firestore errors clearly ---
        console.error("Error loading orders (Check Firestore Indexes?):", error);
        if (error.code === 'failed-precondition') {
            console.error(`Firestore index missing! Create a composite index on (${CLASSIFICATION_FIELD} ASC, ${ORDER_FIELD} ASC) in the Firestore console.`);
            alert(`Firestore index missing! Cannot apply filter '${this.classificationFilter}'. Create a composite index on (${CLASSIFICATION_FIELD} ASC, ${ORDER_FIELD} ASC) in the Firestore console.`);
        }
        // --- End log ---
        this.hasMore = false;
      } finally {
        this.loading = false;
      }
    },

    async getTotalOrderCount(isFiltered = false) {
        // Avoid redundant calls if already loading
        if (this.loading && !isFiltered) return;

        console.log(`Getting total order count ${isFiltered ? `(Filter: ${this.classificationFilter})` : '(Unfiltered)'}...`);
        try {
             const db = getFirestore();
             const baseCollectionRef = collection(db, FIRESTORE_COLLECTION);
             let countQuery;
              if(isFiltered && this.classificationFilter !== 'ALL'){
                 countQuery = query(baseCollectionRef, where(CLASSIFICATION_FIELD, "==", this.classificationFilter));
              } else {
                 // Simple count query without 'where' doesn't need extra indexes
                 countQuery = query(baseCollectionRef);
              }
              const countSnapshot = await getCountFromServer(countQuery);
              this.totalOrders = countSnapshot.data().count;
              console.log(`Total orders count set to: ${this.totalOrders}`);
        } catch(error){
            console.error("Error getting total order count:", error);
             if (error.code === 'failed-precondition' && isFiltered) {
                 console.error(`Firestore index missing for count query! Create an index on (${CLASSIFICATION_FIELD} ASC).`);
                 alert(`Firestore index missing! Cannot get count for filter '${this.classificationFilter}'. Create an index on (${CLASSIFICATION_FIELD} ASC) in the Firestore console.`);
             }
            this.totalOrders = this.orders.length; // Fallback estimate
        }
    },

    async loadMoreOrders() {
      if (this.hasMore && !this.searchQuery) {
        await this.loadOrders();
      }
    },

    async loadAllOrders() {
      if (this.loading || this.searchQuery) return;
      console.log("Starting load all orders...");
      this.bulkLoading = true;
      this.stopBulk = false;
      this.loading = true;

       // Get total count first (consider filter)
       await this.getTotalOrderCount(this.classificationFilter !== 'ALL');

      try {
        while (this.hasMore && !this.stopBulk) {
          const countBefore = this.orders.length;
          await this.loadOrders();
          const countAfter = this.orders.length;
          // Update progress display log
          console.log(`Bulk load progress: ${countAfter} / ${this.totalOrders || countAfter}`);
          await new Promise((resolve) => setTimeout(resolve, 50));

          if (countAfter === countBefore && this.hasMore) {
             console.warn("LoadAllOrders: No new orders loaded, but hasMore is true. Forcing stop.");
             this.hasMore = false;
             break;
          }
        }
        console.log("Finished loading all orders or stopped.");
      } catch (error) {
        console.error("Error loading all orders:", error);
      } finally {
        this.bulkLoading = false;
        this.loading = false;
        this.stopBulk = false;
      }
    },

    stopBulkLoading() {
      console.log("Stopping bulk load...");
      this.stopBulk = true;
    },

    async searchOrders() {
      if (!this.searchQuery) {
        this.resetAndLoad();
        return;
      }
      console.log("Searching orders with query:", this.searchQuery);
      this.searching = true;
      this.loading = true;
      this.orders = [];
      this.hasMore = false;
      this.lastDoc = null;
      try {
        const db = getFirestore();
        // Search by page_id prefix
        const searchQueryRef = query(
          collection(db, FIRESTORE_COLLECTION),
          where(ORDER_FIELD, ">=", this.searchQuery),
          where(ORDER_FIELD, "<=", this.searchQuery + "\uf8ff"),
          orderBy(ORDER_FIELD),
          limit(50) // Limit search results
        );
        const ordersSnapshot = await getDocs(searchQueryRef);
        this.orders = ordersSnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          orderData: docSnap.data()
        }));
        // Update total count for search results view
        this.totalOrders = this.orders.length;
        console.log(`Found ${this.orders.length} orders matching search.`);
      } catch (error) {
        console.error("Error searching orders:", error);
         if (error.code === 'failed-precondition') {
            // This search query requires an index on page_id
            console.error(`Firestore index missing for search! Create an index on (${ORDER_FIELD} ASC).`);
         }
      } finally {
        this.searching = false;
        this.loading = false;
      }
    },

    debouncedSearch: debounce(function () {
      this.searchOrders();
    }, 400),

    filterTree(item, search, textKey) {
        if (!search) return true;
        const name = item[textKey];
        return name && name.toLowerCase().includes(search.toLowerCase());
    },

    async handleActiveChange(newActive) {
      this.imagePreview = null;
      this.documentPreview = null;
      this.selectedOrder = null;
      this.previewLoading = null;

      if (!newActive || newActive.length === 0) {
        this.selectedTab = 2;
        return;
      }

      const selected = newActive[0];
      if (selected.type !== 'order') {
          console.warn("Selected item is not of type 'order':", selected);
          this.selectedTab = 2;
          return;
      }

      this.selectedOrder = this.orders.find(order => order.id === selected.id) || null;

      if (!this.selectedOrder) {
          console.error("Could not find selected order data for ID:", selected.id);
          this.selectedTab = 2;
          return;
      }

      console.log("Active item changed:", selected.type, selected.name, `(ID: ${this.selectedOrder.id})`);
      this.selectedTab = 2; // Default to details while loading

      // --- *** FIX: Construct Image Path *** ---
      let imagePath = null;
      const pageIdForImage = this.selectedOrder.orderData[PAGE_ID_FIELD];
      if (pageIdForImage) {
          imagePath = `/operations/switchorders/pages/${pageIdForImage}.jpg`;
          console.log("Constructed image path:", imagePath);
      } else {
          console.warn("Cannot construct image path: page_id is missing from selected order.", this.selectedOrder.orderData);
      }
      // --- *** END FIX *** ---

      const docPath = this.selectedOrder.orderData[DOCUMENT_PATH_FIELD];
      console.log("Document path from data:", docPath);


      const promises = [];
      // Load Image Preview
      if (imagePath) {
          this.previewLoading = 'image';
          promises.push(
              this.getImageUrl(imagePath).then(url => {
                  this.imagePreview = url;
                  if(this.previewLoading === 'image') this.previewLoading = null;
              }).catch(err => {
                   console.error("Failed to load image preview:", err);
                   if(this.previewLoading === 'image') this.previewLoading = null;
                   this.imagePreview = null; // Ensure reset on error
              })
          );
      } else {
          this.imagePreview = null; // Ensure reset if no path
      }

      // Load Document Preview
       if (docPath) {
          this.previewLoading = 'document';
          promises.push(
              this.getDocumentUrl(docPath).then(url => {
                  this.documentPreview = url;
                  if(this.previewLoading === 'document') this.previewLoading = null;
              }).catch(err => {
                  console.error("Failed to load document preview:", err);
                  if(this.previewLoading === 'document') this.previewLoading = null;
                  this.documentPreview = null; // Ensure reset on error
              })
          );
       } else {
           this.documentPreview = null; // Ensure reset if no path
       }

       await Promise.allSettled(promises);

       // Determine active tab *after* loading attempts
       if(this.imagePreview){
           this.selectedTab = 0;
       } else if(this.documentPreview) {
           this.selectedTab = 1;
       } else {
           this.selectedTab = 2;
       }

       if(this.previewLoading) this.previewLoading = null; // Final cleanup
    },

    getItemColor(item) {
      if (!item || !item.data) return 'black';
      switch (item.data[CLASSIFICATION_FIELD]) {
        case "GOOD": return "green";
        case "BAD": return "red";
        default: return "grey";
      }
    },

    getIconColorByClassification(classification) {
       switch (classification) {
        case "GOOD": return "green";
        case "BAD": return "red";
        default: return "grey";
      }
    },

    async getImageUrl(path) {
      // Path is now constructed before calling this, so it should be the final storage path
      if (!path) return null;
      console.log("Getting image URL for final path:", path);
      try {
          const storage = getStorage();
          // Remove leading slash if present, as ref() doesn't usually start with one
          const storagePath = path.startsWith('/') ? path.substring(1) : path;
          const fileRef = ref(storage, storagePath);
          const url = await getDownloadURL(fileRef);
          console.log("Image URL:", url);
          return url;
      } catch (error) {
          console.error(`Error getting image URL for ${path}:`, error.code, error.message);
          // Handle common errors like 'object-not-found'
          if (error.code === 'storage/object-not-found') {
              console.warn(`Image not found at path: ${path}`);
          }
          return null;
      }
    },

    async getDocumentUrl(path) {
        if (!path) return null;
        console.log("Getting document URL for initial path:", path);
        try {
             // Apply path transformations if needed
             if (path.startsWith("gs://")) {
                path = path.split('/').slice(3).join('/'); // Remove gs://bucket-name/
             } else if (path.startsWith("0-raw/")) {
                 // Example transformation
                 path = `operations/switchorders/dataset/${path.substring("0-raw/".length)}`;
             }
             // Remove leading slash for ref() if present
             const storagePath = path.startsWith('/') ? path.substring(1) : path;
             console.log("Getting document URL for final path:", storagePath);

            const storage = getStorage();
            const fileRef = ref(storage, storagePath);
            const url = await getDownloadURL(fileRef);
            console.log("Document URL:", url);
            // Append #toolbar=0 to attempt hiding PDF controls
            return `${url}#toolbar=0&view=FitH`; // Added FitH view parameter
        } catch (error) {
            console.error(`Error getting document URL for ${path}:`, error.code, error.message);
             if (error.code === 'storage/object-not-found') {
                console.warn(`Document not found at path: ${path} (transformed to ${storagePath})`);
             }
            return null;
        }
    },

    resetAndLoad() {
      console.log("Resetting state and reloading orders...");
      this.searchQuery = "";
      this.searching = false;
      this.orders = [];
      this.active = [];
      this.open = [];
      this.lastDoc = null;
      this.hasMore = true;
      this.bulkLoading = false;
      this.stopBulk = false;
      this.selectedOrder = null;
      this.imagePreview = null;
      this.documentPreview = null;
      this.previewLoading = null;
      this.selectedTab = 0;
      this.totalOrders = 0; // Reset total count
      this.loadOrders(); // Load first page according to current filter
    }
  },
  created() {
    console.log("Component created. Loading initial orders.");
    this.loadOrders();
  },
  computed: {
    progressPercentage() {
      const total = this.totalOrders > 0 ? this.totalOrders : (this.orders.length > 0 ? this.orders.length : 1);
      if (total === 0) return 0;
      return Math.min(100, Math.round((this.orders.length / total) * 100));
    },
    treeItems() {
      // console.log(`Computing treeItems from ${this.orders.length} loaded orders.`);
      return this.orders.map((order) => {
        if (!order || !order.orderData) {
            console.warn("Skipping invalid order item in treeItems:", order);
            return null;
        }
        const pageId = order.orderData[PAGE_ID_FIELD] || `Unknown ID (${order.id})`;
        return {
          id: order.id,
          name: pageId,
          type: 'order',
          data: order.orderData,
          children: [],
        };
      }).filter(item => item !== null);
    },
  },
  watch: {
    classificationFilter(newFilter, oldFilter) {
      if (newFilter !== oldFilter) {
        console.log(`Classification filter changed to: ${newFilter}`);
        if (!this.searchQuery) {
            this.resetAndLoad();
        } else {
             console.log("Filter changed while searching. Clearing search and applying filter.");
             this.resetAndLoad();
        }
      }
    },
    searchQuery(newQuery, oldQuery) {
        if (!newQuery && oldQuery) {
             console.log("Search query cleared. Reloading with current filter.");
            this.resetAndLoad();
        }
    }
  }
}
</script>

<style scoped lang="scss">
.data-column {
  width: 400px;
  min-width: 350px;
  max-width: 500px;
  flex-shrink: 0;
  display: flex; /* Ensure flex properties apply */
  flex-direction: column; /* Stack elements vertically */
  height: 100%; /* Make column take full parent height */
  overflow: hidden; /* Hide overflow, scrolling handled by treeview */
}

.flex-1 {
  flex: 1;
  min-width: 400px;
  height: 100%; /* Make panel take full parent height */
  display: flex; /* Enable flex for vertical stacking */
  flex-direction: column; /* Stack tabs and content */
}

.the-thin-grey-border {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.v-text-field {
  flex-grow: 0; /* Prevent text field from growing */
  flex-shrink: 0; /* Prevent text field from shrinking */
}

/* Make tabs items container fill remaining space */
.v-tabs-items {
  flex-grow: 1; /* Take available vertical space */
  background-color: #fafafa !important;
   overflow-y: auto; /* Allow internal scrolling if needed, though tab-item handles it too */
}

/* Ensure individual tab items can fill height and scroll */
.v-window-item.fill-height {
    height: 100%;
    overflow-y: auto; /* Allow scrolling within the tab content */
    display: flex; /* Use flex for centering content */
    flex-direction: column; /* Stack content vertically */
}


img, iframe {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  background-color: #eeeeee;
  /* Remove fixed height/width if parent container handles sizing */
  /* width: 100%; */
  /* height: 100%; */
}

iframe {
  border: none;
  flex-grow: 1; /* Allow iframe to fill space in flex container */
}

pre {
  white-space: pre-wrap;
  word-wrap: break-word;
  background-color: #f5f5f5;
  padding: 12px; /* Increased padding */
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.9em; /* Slightly larger font */
  overflow-x: auto;
  flex-grow: 1; /* Allow pre tag to fill space */
}

.v-btn-toggle {
  width: 100%;
  display: flex;
  flex-shrink: 0; /* Prevent shrinking */
}
.v-btn-toggle .v-btn {
  flex: 1;
}

.v-progress-linear strong {
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
}

/* Center content vertically and horizontally */
.justify-center {
    justify-content: center;
}
.align-center {
    align-items: center;
}
.height-100 {
  height: 100% !important;
}

.image-preview-container .v-window__container {
  height: 100% !important;
}
.v-window__container {
  height: 100% !important;
}
</style>